import { useSubject, useAppStorage, useObservable } from '@aesop-fables/scrinium';
import {
  PricebookBundle,
  AvailablePricebooks,
  PricebookBundleWithUsage,
} from '../models/PricebookData';
import {
  BasePricebookOptionsSubject,
  PricebookOptionsSubject,
  PricebookSelectionsSubject,
  OrgAvailablePricebooksSubject,
  GroupAvailablePricebooksSubject,
  TeamAvailablePricebooksSubject,
  TeamPricebooksInUseSubject,
  AllPricebookBundlesSubject,
} from '../data/pricebook/subjects/PricebookSubject';
import { AdminTierTypeEnum } from '../services/drillDown';
import { PricebookCompartments, pricebookStorageKey } from '../data/pricebook';

export const usePricebookOptions = () =>
  useSubject<PricebookBundle[] | undefined>(PricebookOptionsSubject);

export const usePricebookSelections = () =>
  useSubject<PricebookBundle[] | undefined>(PricebookSelectionsSubject);

// filters out add-on pricebooks
export const useBasePricebookOptions = () =>
  useSubject<PricebookBundle[] | undefined>(BasePricebookOptionsSubject);

export const useOrgAvailablePricebooks = () =>
  useSubject<AvailablePricebooks | undefined>(OrgAvailablePricebooksSubject);

export const useTeamAvailablePricebooks = () =>
  useSubject<AvailablePricebooks | undefined>(TeamAvailablePricebooksSubject);

export const useTeamPricebooksInUse = () =>
  useSubject<PricebookBundleWithUsage[] | undefined>(TeamPricebooksInUseSubject);

export const useGroupAvailablePricebooks = () =>
  useSubject<AvailablePricebooks | undefined>(GroupAvailablePricebooksSubject);

export const useAllPricebookBundles = () =>
  useSubject<PricebookBundle[] | undefined>(AllPricebookBundlesSubject);

export const useAvailablePricebooks = (adminTier: AdminTierTypeEnum | undefined) => {
  const orgPricebooks = useOrgAvailablePricebooks();
  const teamPricebooks = useTeamAvailablePricebooks();
  const groupPricebooks = useGroupAvailablePricebooks();

  switch (adminTier) {
    case AdminTierTypeEnum.Organization:
      return orgPricebooks;
    case AdminTierTypeEnum.Team:
      return teamPricebooks;
    case AdminTierTypeEnum.Group:
      return groupPricebooks;
    default:
      return undefined;
  }
};

export const usePricebooksInitialized = (adminTier: AdminTierTypeEnum | undefined) => {
  const appStorage = useAppStorage();
  const pricebookCache = appStorage.retrieve<PricebookCompartments>(pricebookStorageKey);

  const orgInitialized = useObservable(
    pricebookCache.findCompartment('orgAvailablePricebooks').initialized$,
  );
  const teamInitialized = useObservable(
    pricebookCache.findCompartment('teamAvailablePricebooks').initialized$,
  );
  const groupInitialized = useObservable(
    pricebookCache.findCompartment('groupAvailablePricebooks').initialized$,
  );

  switch (adminTier) {
    case AdminTierTypeEnum.Organization:
      return orgInitialized;
    case AdminTierTypeEnum.Team:
      return teamInitialized;
    case AdminTierTypeEnum.Group:
      return groupInitialized;
    default:
      return true;
  }
};
